import { Country } from 'country-state-city';

// Resolve a country's ISO-2 code from its display name (the value stored in the form)
const getCountryIso = (countryName) =>
  countryName
    ? Country.getAllCountries().find((c) => c.name === countryName)?.isoCode ||
      ''
    : '';

// Per-country postal code rules, keyed by ISO-2 code.
//  - format: normalize raw input to the country's canonical style
//  - regex:  validate the formatted value
// Countries not listed here accept any input (format passes through trimmed).
const POSTAL_RULES = {
  // Netherlands: 4 digits + 2 letters, always rendered as "XXXX YY"
  NL: {
    format: (value) => {
      const cleaned = value.replace(/\s+/g, '').toUpperCase();
      const match = cleaned.match(/^(\d{4})([A-Za-z]{2})$/);
      return match ? `${match[1]} ${match[2]}` : cleaned;
    },
    regex: /^\d{4} [A-Z]{2}$/,
  },
  // Spain: 5 digits
  ES: {
    format: (value) => value.replace(/\s+/g, ''),
    regex: /^\d{5}$/,
  },
  // Luxembourg: "L-" prefix + 4 digits, e.g. "L-1234"
  // (accepts input with or without the prefix, always stored with it)
  LU: {
    format: (value) => {
      const digits = value
        .replace(/\s+/g, '')
        .toUpperCase()
        .replace(/^L-?/, '');
      return /^\d{4}$/.test(digits) ? `L-${digits}` : digits;
    },
    regex: /^L-\d{4}$/,
  },
  // United Kingdom: outward + space + inward (3 chars), e.g. "SW1A 1AA"
  GB: {
    format: (value) => {
      const cleaned = value.replace(/\s+/g, '').toUpperCase();
      return cleaned.length > 3
        ? `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`
        : cleaned;
    },
    regex: /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/,
  },
};

// Format a postal code according to the selected country's style.
// e.g. NL "1234ab" -> "1234 AB". Unknown countries are returned trimmed.
export const formatPostalCode = (value, countryName) => {
  if (!value) return value;
  const rule = POSTAL_RULES[getCountryIso(countryName)];
  return rule ? rule.format(value.trim()) : value.trim();
};

// Validate an (already formatted) postal code for the selected country.
// Returns true when no rule exists for the country, so unknown countries pass.
export const isValidPostalCode = (value, countryName) => {
  if (!value || value.trim() === '') return true; // emptiness handled by required rules
  const rule = POSTAL_RULES[getCountryIso(countryName)];
  return rule ? rule.regex.test(value.trim()) : true;
};
